import { confirmDialog } from '../../../ui/dialogs';
import { hasPerm } from '../../session';
import type { AssistantFinding } from '../api';
import { conversationStudy } from '../context';
import { dateText, followupBody, reviewedLabel, RIS_PERM } from './logic';
import { sendLabel, sendToReport } from './send';
import type { FollowupSuggestion } from './shapes';
import { busy, button, el, loading, type RadiologistDeps, textBlock, toolCard, toolFailure, traceNodes } from './ui';

/**
 * "Seguimiento" on a reviewed finding (contract v3 §2.6): ris asks the model which guideline applies
 * and what follow-up it recommends. The card shows the guideline, the interval, the recommendation
 * text and its citation; "Crear seguimiento" (confirmed, viewer:ai:review) stores a pending
 * follow-up for the patient, and "Enviar al informe" sends the recommendation to the draft's
 * Recomendaciones section through §2.1.
 */

export const FOLLOWUP_TEXT = {
  button: 'Seguimiento',
  title: 'Seguimiento por guía',
  loading: 'consultando la guía…',
  guideline: 'Guía',
  interval: (months: number) => (months === 1 ? 'Control en 1 mes' : `Control en ${months} meses`),
  noFollowup: 'La guía no indica seguimiento para este hallazgo.',
  modality: 'Modalidad',
  recommendation: 'Recomendación sugerida:',
  citation: 'Referencia',
  create: 'Crear seguimiento',
  creating: 'Creando…',
  created: (due: string) => `Seguimiento creado, vence ${due}.`,
  createTitle: 'Crear seguimiento',
  createIntro: 'Se creará un seguimiento pendiente para este paciente:',
  confirm: 'Crear',
  cancel: 'Cancelar',
  reviewOnly: 'Crear el seguimiento requiere permiso para revisar hallazgos.',
  empty: 'El Asistente Guru no devolvió una recomendación de seguimiento.',
  failed: 'No fue posible consultar el seguimiento.',
  createFailed: 'No fue posible crear el seguimiento.',
} as const;

function details(s: FollowupSuggestion): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (s.guideline) out.push(el('div', 'assistant-followup-guideline', `${FOLLOWUP_TEXT.guideline}: ${s.guideline}`));
  const interval = s.intervalMonths > 0 ? FOLLOWUP_TEXT.interval(s.intervalMonths) : FOLLOWUP_TEXT.noFollowup;
  out.push(el('div', 'assistant-followup-interval', s.modality && s.intervalMonths > 0 ? `${interval} · ${FOLLOWUP_TEXT.modality}: ${s.modality}` : interval));
  if (s.text) out.push(textBlock(FOLLOWUP_TEXT.recommendation, s.text, 'assistant-followup-text'));
  if (s.citation) out.push(el('div', 'assistant-trace assistant-followup-citation', `${FOLLOWUP_TEXT.citation}: ${s.citation}`));
  return out;
}

async function create(deps: RadiologistDeps, finding: AssistantFinding, s: FollowupSuggestion, trigger: HTMLButtonElement, study: string): Promise<void> {
  const body = followupBody(finding, s);
  if (!body) return;
  const summary = [FOLLOWUP_TEXT.interval(body.due_in_months), body.modality ? `${FOLLOWUP_TEXT.modality}: ${body.modality}` : '',
    body.guideline ? `${FOLLOWUP_TEXT.guideline}: ${body.guideline}` : '', body.reason].filter(Boolean).join('\n');
  const confirmed = await confirmDialog(deps.shell, {
    title: FOLLOWUP_TEXT.createTitle, text: `${FOLLOWUP_TEXT.createIntro}\n\n${summary}`,
    confirmLabel: FOLLOWUP_TEXT.confirm, cancelLabel: FOLLOWUP_TEXT.cancel, className: 'assistant-insert-dialog assistant-followup-dialog',
  });
  if (!confirmed) return;
  const stored = await busy(trigger, FOLLOWUP_TEXT.creating, async () => {
    try {
      return await deps.api.createFollowup(study, body);
    } catch (error) {
      console.warn('[viewer] follow-up create failed', error);
      deps.shell.toast(toolFailure(error, 'followups', FOLLOWUP_TEXT.createFailed), 'error');
      return undefined;
    }
  });
  if (stored === undefined) return;
  // Created: the button stays done so a second click cannot store the same follow-up twice.
  trigger.disabled = true;
  trigger.textContent = FOLLOWUP_TEXT.created(dateText(stored?.dueOn ?? null));
  deps.shell.toast(FOLLOWUP_TEXT.created(dateText(stored?.dueOn ?? null)));
}

function actions(deps: RadiologistDeps, finding: AssistantFinding, s: FollowupSuggestion, study: string): HTMLElement {
  const row = el('div', 'assistant-finding-actions');
  if (s.intervalMonths > 0) {
    const make: HTMLButtonElement = button(FOLLOWUP_TEXT.create, 'assistant-followup-create', () => void create(deps, finding, s, make, study));
    if (!hasPerm(RIS_PERM.review)) {
      make.disabled = true;
      make.title = FOLLOWUP_TEXT.reviewOnly;
    }
    row.appendChild(make);
  }
  const text = s.text;
  if (text) row.appendChild(button(sendLabel(), 'assistant-followup-send', () => void sendToReport(deps.shell, deps.api, [{ section: 'recommendations', text }], study)));
  return row;
}

export async function showFollowup(deps: RadiologistDeps, finding: AssistantFinding): Promise<void> {
  const study = conversationStudy(deps.shell);
  const { body } = toolCard(deps, `followup-${finding.id}`, `${FOLLOWUP_TEXT.title}: ${reviewedLabel(finding)}`, study);
  loading(body, FOLLOWUP_TEXT.loading);
  try {
    const s = await deps.api.followupSuggestion(finding.id);
    if (!s) return void body.replaceChildren(el('p', 'assistant-empty', FOLLOWUP_TEXT.empty));
    body.replaceChildren(...details(s), ...traceNodes(s), actions(deps, finding, s, study));
  } catch (error) {
    console.warn('[viewer] follow-up suggestion failed', error);
    body.replaceChildren(el('p', 'assistant-tool-note assistant-tool-error', toolFailure(error, 'followups', FOLLOWUP_TEXT.failed)));
  }
}
